import { QueryStatus } from '@tanstack/react-query'
import { UsePaginationReturn } from '@vtex/admin-ui'
import { usePromotions } from 'hooks'
import { useContext, useEffect, useMemo } from 'react'
import { ITEMS_PER_PAGE } from 'shared'
import type { SimplifiedPromotion } from 'typings'
import { PromotionsContext } from '../../providers/PromotionsContextProvider'
import { filterByText } from './Table/Search'

function updatePagination(
  paginationState: UsePaginationReturn,
  items: SimplifiedPromotion[]
) {
  const total = items.length
  const { paginate, currentPage } = paginationState

  paginate({
    type: 'setTotal',
    total: items.length,
  })

  const isPageValid = currentPage <= Math.ceil(total / ITEMS_PER_PAGE)

  if (!isPageValid) {
    paginate({ type: 'reset' })
  }
}

type RequestStatus = 'idle' | QueryStatus
type DataViewStatus = 'ready' | 'loading' | 'not-found' | 'empty' | 'error'

function getTableStatus(
  originalItemsLength: number,
  currentItemsLength: number,
  requestStatus: RequestStatus
): DataViewStatus | undefined {
  switch (requestStatus) {
    case 'pending':
      return 'loading'

    case 'error':
      return 'error'

    case 'success':
      if (currentItemsLength) return 'ready'

      if (!currentItemsLength && originalItemsLength) return 'not-found'

      return 'empty'
  }
}

export function usePromotionsListState() {
  const context = useContext(PromotionsContext)
  const { data, status } = usePromotions()
  const promotions = useMemo(() => data?.items ?? [], [data?.items])

  if (context === undefined) {
    throw new Error(
      'PromotionsContext must be used within a PromotionsContextProvider'
    )
  }

  const {
    paginationState,
    setItems,
    filtersState,
    tableState,
    searchState,
    viewState,
  } = context

  useEffect(() => {
    const type = getTableStatus(
      promotions.length,
      tableState.data.length,
      status
    )

    type && viewState.setStatus({ type })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotions.length, status, tableState.data.length])

  useEffect(() => {
    let filteredItems = filtersState.reduce(
      (acc, filter) => acc.filter(filter.filterFn),
      promotions
    )

    const textSearch = searchState.debouncedValue.trim()

    if (!!textSearch.length) {
      filteredItems = filteredItems.filter(filterByText(textSearch))
    }

    setItems(filteredItems)
  }, [filtersState, promotions, searchState.debouncedValue, setItems])

  useEffect(() => {
    tableState.status === 'ready' &&
      updatePagination(paginationState, tableState.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.data])

  return { ...context, originalItems: promotions }
}
